import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getIn, useFormikContext } from 'formik';
import { AlignLeft, CheckSquare, Circle, GripVertical, Hash, Link2, List, Mail, Plus, Type, X } from 'lucide-react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Text,
  TextArea,
} from '@/design-system/components/ui';

import { AffiliateApplicationFormFieldType } from '../../domain/affiliate-application-types';
import { FormFieldValues } from './FormBuilderView';

interface FormBuilderValues {
  formFields: FormFieldValues[];
}

export interface FormFieldItemProps {
  field: FormFieldValues;
  index: number;
  expanded: string;
  onExpand: (value: string) => void;
  onDelete: () => void;
}

const getFieldTypeIcon = (type: AffiliateApplicationFormFieldType) => {
  const iconProps = { size: 18, className: 'tw:text-muted-foreground', 'aria-hidden': true as const };
  switch (type) {
    case 'text':
      return <Type {...iconProps} />;
    case 'email':
      return <Mail {...iconProps} />;
    case 'textarea':
      return <AlignLeft {...iconProps} />;
    case 'number':
      return <Hash {...iconProps} />;
    case 'select':
      return <List {...iconProps} />;
    case 'checkbox':
      return <CheckSquare {...iconProps} />;
    case 'radio':
      return <Circle {...iconProps} />;
    case 'url':
      return <Link2 {...iconProps} />;
    default:
      return <Type {...iconProps} />;
  }
};

export const FormFieldItem = ({ field, index, expanded, onExpand, onDelete }: FormFieldItemProps) => {
  const formik = useFormikContext<FormBuilderValues>();

  const fieldErrors = getIn(formik.errors, `formFields.${index}`);
  const fieldTouched = getIn(formik.touched, `formFields.${index}`);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
  });

  const sortableStyle = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleTypeChange = (newType: AffiliateApplicationFormFieldType) => {
    formik.setFieldValue(`formFields.${index}.type`, newType);
    if (newType !== 'select' && newType !== 'radio') {
      formik.setFieldValue(`formFields.${index}.options`, []);
    }
    if ((newType === 'select' || newType === 'radio') && field.options.length === 0) {
      formik.setFieldValue(`formFields.${index}.options`, ['']);
    }
    if (newType === 'number' || newType === 'checkbox') {
      formik.setFieldValue(`formFields.${index}.validation.minLength`, null);
      formik.setFieldValue(`formFields.${index}.validation.maxLength`, null);
      formik.setFieldValue(`formFields.${index}.validation.pattern`, '');
    }
    if (newType !== 'number') {
      formik.setFieldValue(`formFields.${index}.validation.min`, null);
      formik.setFieldValue(`formFields.${index}.validation.max`, null);
    }
  };

  const stringValidationTypes: AffiliateApplicationFormFieldType[] = ['text', 'textarea', 'email', 'url'];
  const showStringValidation = stringValidationTypes.includes(field.type);

  return (
    <div ref={setNodeRef} style={sortableStyle}>
      <Accordion type="single" collapsible value={isDragging ? '' : expanded} onValueChange={onExpand}>
        <AccordionItem value={field.id} className="tw:border-0">
          <Card className={fieldTouched && fieldErrors ? 'tw:border-destructive' : ''}>
            <CardContent className="tw:p-0">
              {/* Header: relative wrapper with trigger full-width, drag/trash absolutely positioned */}
              <div className="tw:relative">
                <AccordionTrigger className="tw:py-6 tw:px-14 tw:hover:no-underline tw:w-full">
                  <div className="tw:flex tw:items-center tw:gap-3 tw:w-full">
                    {getFieldTypeIcon(field.type)}
                    <Text variant="body-md" weight="bold" className="tw:truncate">
                      {field.label || 'Untitled Field'}
                    </Text>
                    <div className="tw:flex tw:items-center tw:gap-2 tw:ml-auto tw:mr-2">
                      <Badge variant="default" className="tw:uppercase">
                        {field.type}
                      </Badge>
                      <Badge variant="outline">{field.required ? 'Required' : 'Optional'}</Badge>
                    </div>
                  </div>
                </AccordionTrigger>

                <button
                  type="button"
                  aria-label="Drag to reorder"
                  className="tw:absolute tw:left-4 tw:top-1/2 tw:-translate-y-1/2 tw:cursor-grab tw:text-muted-foreground tw:hover:text-foreground"
                  {...attributes}
                  {...listeners}
                  onPointerDown={(e) => {
                    onExpand('');
                    listeners?.onPointerDown?.(e as never);
                  }}
                >
                  <GripVertical size={20} aria-hidden="true" />
                </button>

                <button
                  type="button"
                  aria-label="Delete field"
                  className="tw:absolute tw:right-4 tw:top-1/2 tw:-translate-y-1/2 tw:p-1 tw:rounded tw:text-muted-foreground tw:hover:text-destructive tw:transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                >
                  <X size={20} aria-hidden="true" />
                </button>
              </div>

              <AccordionContent>
                <div className="tw:flex tw:flex-col tw:gap-4 tw:px-4 tw:pt-2">
                  {/* Field Type — full width */}
                  <div className="tw:flex tw:flex-col tw:gap-1.5">
                    <Label>Field Type</Label>
                    <Select
                      value={field.type}
                      onValueChange={(val) => handleTypeChange(val as AffiliateApplicationFormFieldType)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="text">Text</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="textarea">Textarea</SelectItem>
                        <SelectItem value="number">Number</SelectItem>
                        <SelectItem value="select">Select</SelectItem>
                        <SelectItem value="radio">Radio</SelectItem>
                        <SelectItem value="checkbox">Checkbox</SelectItem>
                        <SelectItem value="url">URL</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Row: Label + Description */}
                  <div className="tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:gap-6">
                    <Input
                      label="Label"
                      value={field.label}
                      onChange={(e) => formik.setFieldValue(`formFields.${index}.label`, e.target.value)}
                      onBlur={() => formik.setFieldTouched(`formFields.${index}.label`, true)}
                      placeholder="Field label"
                      required
                      error={fieldTouched?.label && fieldErrors?.label ? true : undefined}
                      errorMessage={fieldTouched?.label && fieldErrors?.label ? fieldErrors.label : undefined}
                    />
                    <Input
                      label="Description"
                      value={field.description}
                      onChange={(e) => formik.setFieldValue(`formFields.${index}.description`, e.target.value)}
                      placeholder="Help text for this field"
                    />
                  </div>

                  {/* Row: Placeholder (if applicable) + Required field */}
                  <div className="tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:gap-6 tw:items-center">
                    {field.type !== 'radio' && field.type !== 'checkbox' ? (
                      <Input
                        label="Placeholder"
                        value={field.placeholder}
                        onChange={(e) => formik.setFieldValue(`formFields.${index}.placeholder`, e.target.value)}
                        placeholder="Placeholder text"
                      />
                    ) : (
                      <div />
                    )}
                    <div className="tw:flex tw:items-center tw:justify-between">
                      <div className="tw:flex tw:flex-col">
                        <Text variant="body-sm" weight="medium">
                          Required field
                        </Text>
                        <Text variant="caption" color="muted">
                          Applicants must fill this
                        </Text>
                      </div>
                      <Switch
                        checked={field.required}
                        onCheckedChange={(checked) => formik.setFieldValue(`formFields.${index}.required`, checked)}
                      />
                    </div>
                  </div>

                  {/* Options — for select/radio */}
                  {(field.type === 'select' || field.type === 'radio') && (
                    <div className="tw:flex tw:flex-col tw:gap-4">
                      <Label>Options</Label>
                      {field.options.map((option, optionIndex) => (
                        <div key={`${field.id}-opt-${optionIndex}`} className="tw:flex tw:items-center tw:gap-3">
                          <Text variant="body-md" weight="medium" color="muted" className="tw:flex-shrink-0">
                            #{optionIndex + 1}
                          </Text>
                          <Input
                            value={option}
                            onChange={(e) => {
                              const newOptions = [...field.options];
                              newOptions[optionIndex] = e.target.value;
                              formik.setFieldValue(`formFields.${index}.options`, newOptions);
                            }}
                            placeholder={`Option ${optionIndex + 1}`}
                            className="tw:flex-1"
                          />
                          {field.options.length > 1 && (
                            <button
                              type="button"
                              aria-label="Remove option"
                              className="tw:flex-shrink-0 tw:p-2 tw:rounded tw:text-muted-foreground tw:hover:text-destructive tw:transition-colors"
                              onClick={() => {
                                const newOptions = field.options.filter((_, i) => i !== optionIndex);
                                formik.setFieldValue(`formFields.${index}.options`, newOptions);
                              }}
                            >
                              <X size={18} aria-hidden="true" />
                            </button>
                          )}
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        className="tw:w-full tw:border-dashed"
                        onClick={() => {
                          formik.setFieldValue(`formFields.${index}.options`, [...field.options, '']);
                        }}
                      >
                        <Plus size={14} aria-hidden="true" />
                        Add Option
                      </Button>
                      {fieldTouched?.options && typeof fieldErrors?.options === 'string' && (
                        <Text variant="body-sm" className="tw:text-destructive">
                          {fieldErrors.options}
                        </Text>
                      )}
                    </div>
                  )}

                  {/* Validation — string types (two columns) */}
                  {showStringValidation && (
                    <div className="tw:flex tw:flex-col tw:gap-2">
                      <Label>Validation</Label>
                      <div className="tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:gap-4">
                        <Input
                          label="Min Length"
                          type="number"
                          value={field.validation.minLength ?? ''}
                          onChange={(e) =>
                            formik.setFieldValue(
                              `formFields.${index}.validation.minLength`,
                              e.target.value ? Number(e.target.value) : null,
                            )
                          }
                        />
                        <Input
                          label="Max Length"
                          type="number"
                          value={field.validation.maxLength ?? ''}
                          onChange={(e) =>
                            formik.setFieldValue(
                              `formFields.${index}.validation.maxLength`,
                              e.target.value ? Number(e.target.value) : null,
                            )
                          }
                        />
                        <Input
                          label="Pattern (regex)"
                          value={field.validation.pattern}
                          onChange={(e) =>
                            formik.setFieldValue(`formFields.${index}.validation.pattern`, e.target.value)
                          }
                          placeholder="e.g. ^[a-zA-Z]+$"
                        />
                      </div>
                    </div>
                  )}

                  {/* Validation — number type (two columns) */}
                  {field.type === 'number' && (
                    <div className="tw:flex tw:flex-col tw:gap-2">
                      <Label>Validation</Label>
                      <div className="tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:gap-4">
                        <Input
                          label="Min Value"
                          type="number"
                          value={field.validation.min ?? ''}
                          onChange={(e) =>
                            formik.setFieldValue(
                              `formFields.${index}.validation.min`,
                              e.target.value ? Number(e.target.value) : null,
                            )
                          }
                        />
                        <Input
                          label="Max Value"
                          type="number"
                          value={field.validation.max ?? ''}
                          onChange={(e) =>
                            formik.setFieldValue(
                              `formFields.${index}.validation.max`,
                              e.target.value ? Number(e.target.value) : null,
                            )
                          }
                        />
                      </div>
                    </div>
                  )}
                  {/* Live Snippet Preview */}
                  <div className="tw:rounded-xl tw:border tw:border-border/50 tw:bg-muted/30 tw:p-4">
                    <Text variant="body-sm" weight="bold" className="tw:mb-3">
                      Live Snippet Preview
                    </Text>
                    <div className="tw:flex tw:flex-col tw:gap-2">
                      {field.label && (
                        <Text variant="body-sm" weight="medium">
                          {field.label}
                          {field.required && <span className="tw:text-destructive tw:ml-0.5">*</span>}
                        </Text>
                      )}
                      {field.description && (
                        <Text variant="caption" color="muted">
                          {field.description}
                        </Text>
                      )}

                      {(field.type === 'text' ||
                        field.type === 'email' ||
                        field.type === 'url' ||
                        field.type === 'number') && (
                        <Input
                          placeholder={field.placeholder || `Enter ${field.type}...`}
                          type={field.type === 'number' ? 'number' : 'text'}
                          disabled
                        />
                      )}

                      {field.type === 'textarea' && (
                        <TextArea placeholder={field.placeholder || 'Enter text...'} rows={3} disabled />
                      )}

                      {field.type === 'select' && (
                        <Select disabled>
                          <SelectTrigger>
                            <SelectValue placeholder={field.placeholder || 'Select an option...'} />
                          </SelectTrigger>
                          <SelectContent>
                            {field.options.filter(Boolean).map((opt, i) => (
                              <SelectItem key={i} value={opt}>
                                {opt}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}

                      {field.type === 'radio' && (
                        <RadioGroup disabled>
                          {field.options.filter(Boolean).map((opt, i) => (
                            <div key={i} className="tw:flex tw:items-center tw:gap-2">
                              <RadioGroupItem value={opt} id={`preview-${field.id}-${i}`} />
                              <Label htmlFor={`preview-${field.id}-${i}`}>{opt}</Label>
                            </div>
                          ))}
                          {field.options.filter(Boolean).length === 0 && (
                            <Text variant="caption" color="muted">
                              Add options to see preview
                            </Text>
                          )}
                        </RadioGroup>
                      )}

                      {field.type === 'checkbox' && (
                        <div className="tw:flex tw:items-center tw:gap-2">
                          <Checkbox disabled />
                          <Label>{field.label || 'Checkbox label'}</Label>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </CardContent>
          </Card>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

FormFieldItem.displayName = 'FormFieldItem';
